import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { inr, convertFileToBase64 } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminCMS() {
  return (
    <div data-testid="admin-cms">
      <div className="label-eyebrow mb-2">Content Management</div>
      <h1 className="text-3xl font-heading text-white mb-8">Testimonials CMS</h1>
      <TestimonialsCMS />
    </div>
  );
}

// ---- Services ----
const emptyService = { name: "", slug: "", category: "massage", description: "", duration_min: 60, price: 0, image: "", status: "active" };
function ServicesCMS() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyService);
  const [editing, setEditing] = useState(null);
  const load = async () => setList((await api.get("/services")).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing) await api.put(`/admin/services/${editing}`, { ...form, duration_min: +form.duration_min, price: +form.price });
      else await api.post("/admin/services", { ...form, duration_min: +form.duration_min, price: +form.price });
      toast.success("Saved");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="mt-6">
      <button onClick={() => { setEditing(null); setForm(emptyService); setOpen(true); }} className="gold-btn mb-4" data-testid="cms-service-add"><Plus size={14}/> Add service</button>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-hpaper border-b border-white/10"><tr>{["Name", "Category", "Duration", "Price", ""].map((h) => <th key={h} className="text-left label-eyebrow px-4 py-3 text-white/60">{h}</th>)}</tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-b border-white/5">
                <td className="px-4 py-3 text-white">{s.name}</td>
                <td className="px-4 py-3 text-white/60 text-xs uppercase tracking-widest">{s.category}</td>
                <td className="px-4 py-3 text-white/70">{s.duration_min} min</td>
                <td className="px-4 py-3 text-hgold">{inr(s.price)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => { setEditing(s.id); setForm(s); setOpen(true); }} className="text-white/60 hover:text-hgold mr-3"><Pencil size={14}/></button>
                  <button onClick={() => del(s.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-hpaper border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Add"} service</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
            <input placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
            <input placeholder="Category (massage / body_care / grooming / access)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
            <textarea rows="3" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none resize-none"/>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" placeholder="Duration (min)" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
              <input type="number" placeholder="Price (₹)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-white/50 block">Service Image</label>
              {form.image ? (
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-20 h-20 border border-white/10 overflow-hidden bg-black flex items-center justify-center">
                    <img src={form.image} alt="Preview" className="max-w-full max-h-full object-contain" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image: "" })}
                    className="text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10 px-2 py-1 uppercase tracking-wider"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        const base64 = await convertFileToBase64(file);
                        setForm({ ...form, image: base64 });
                      } catch {
                        toast.error("Failed to read file");
                      }
                    }
                  }}
                  className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none text-xs text-white/70"
                />
              )}
            </div>
            <button onClick={save} className="gold-btn w-full justify-center">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Packages ----
const emptyPkg = { name: "", slug: "", color: "#D4AF37", description: "", duration_min: 90, spa_price: 0, home_price: 0, included_services: [], image: "", status: "active" };
function PackagesCMS() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPkg);
  const [editing, setEditing] = useState(null);
  const load = async () => setList((await api.get("/packages")).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const payload = { ...form, duration_min: +form.duration_min, spa_price: +form.spa_price, home_price: +form.home_price };
      if (editing) await api.put(`/admin/packages/${editing}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/packages/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="mt-6">
      <button onClick={() => { setEditing(null); setForm(emptyPkg); setOpen(true); }} className="gold-btn mb-4"><Plus size={14}/> Add package</button>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((p) => (
          <div key={p.id} className="border border-white/10 p-4 bg-hpaper">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-heading text-white text-lg">{p.name}</div>
                <div className="text-xs text-white/50 mt-1">{p.duration_min} min · Spa {inr(p.spa_price)} / Home {inr(p.home_price)}</div>
              </div>
              <div className="w-3 h-3 rounded-full" style={{ background: p.color }}/>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setEditing(p.id); setForm({ ...emptyPkg, ...p }); setOpen(true); }} className="text-white/60 hover:text-hgold text-xs uppercase tracking-widest">Edit</button>
              <button onClick={() => del(p.id)} className="text-red-400 hover:text-red-300 text-xs uppercase tracking-widest">Delete</button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-hpaper border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Add"} package</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
              <input placeholder="Color hex" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
            </div>
            <textarea rows="3" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none resize-none"/>
            <div className="grid grid-cols-3 gap-3">
              <input type="number" placeholder="Duration" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
              <input type="number" placeholder="Spa price" value={form.spa_price} onChange={(e) => setForm({ ...form, spa_price: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
              <input type="number" placeholder="Home price" value={form.home_price} onChange={(e) => setForm({ ...form, home_price: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
            </div>
            <input placeholder="Included services (comma separated)" value={form.included_services?.join(", ")} onChange={(e) => setForm({ ...form, included_services: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
            <div className="space-y-1">
              <label className="text-xs text-white/50 block">Package Image</label>
              {form.image ? (
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-20 h-20 border border-white/10 overflow-hidden bg-black flex items-center justify-center">
                    <img src={form.image} alt="Preview" className="max-w-full max-h-full object-contain" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image: "" })}
                    className="text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10 px-2 py-1 uppercase tracking-wider"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        const base64 = await convertFileToBase64(file);
                        setForm({ ...form, image: base64 });
                      } catch {
                        toast.error("Failed to read file");
                      }
                    }
                  }}
                  className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none text-xs text-white/70"
                />
              )}
            </div>
            <button onClick={save} className="gold-btn w-full justify-center">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Blog ----
const emptyBlog = { title: "", slug: "", excerpt: "", content: "", category: "wellness", tags: [], cover_image: "", author: "Hammam Editorial", status: "published" };
function BlogCMS() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyBlog);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/blog");
      setList(r.data);
    } catch {
      toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/blog/${editing}`, form);
      else await api.post("/admin/blog", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/admin/blog/${id}`); toast.success("Deleted"); load(); };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-white/40 uppercase tracking-widest font-heading">Loading Blog Posts...</div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <button onClick={() => { setEditing(null); setForm(emptyBlog); setOpen(true); }} className="gold-btn mb-4"><Plus size={14}/> New post</button>
      <div className="space-y-3">
        {list.map((p) => (
          <div key={p.id} className="border border-white/10 p-5 bg-hpaper flex justify-between items-center">
            <div>
              <div className="font-heading text-white text-lg">{p.title}</div>
              <div className="text-xs text-white/50 mt-1">{p.category} · {(p.created_at || "").slice(0,10)}</div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setEditing(p.id); setForm({ ...emptyBlog, ...p }); setOpen(true); }} className="text-white/60 hover:text-hgold"><Pencil size={14}/></button>
              <button onClick={() => del(p.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-hpaper border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "New"} post</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
              <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none"/>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-white/50 block">Cover Image</label>
              {form.cover_image ? (
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-20 h-20 border border-white/10 overflow-hidden bg-black flex items-center justify-center">
                    <img src={form.cover_image} alt="Preview" className="max-w-full max-h-full object-contain" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, cover_image: "" })}
                    className="text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10 px-2 py-1 uppercase tracking-wider"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        const base64 = await convertFileToBase64(file);
                        setForm({ ...form, cover_image: base64 });
                      } catch {
                        toast.error("Failed to read file");
                      }
                    }
                  }}
                  className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none text-xs text-white/70"
                />
              )}
            </div>
            <textarea rows="2" placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none resize-none"/>
            <textarea rows="10" placeholder="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="w-full bg-black border border-white/10 focus:border-hgold px-3 py-2 outline-none resize-none"/>
            <button onClick={save} className="gold-btn w-full justify-center">Publish</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Testimonials ----
function TestimonialsCMS() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ author: "", location: "", rating: 5, quote: "", status: "published" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/testimonials");
      setList(r.data);
    } catch {
      toast.error("Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.author || !form.quote) return toast.error("Author name and quote are required");
    try {
      await api.post("/admin/testimonials", { ...form, rating: +form.rating });
      toast.success("Testimonial added successfully");
      setForm({ author: "", location: "", rating: 5, quote: "", status: "published" });
      load();
    } catch { toast.error("Failed to save testimonial"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Testimonial deleted");
    load();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-amber-400 font-bold uppercase tracking-widest animate-pulse">Loading Testimonials...</div>
      </div>
    );
  }

  const ratingOptions = [
    { label: "★ ★ ★ ★ ★  (5 Stars)", value: 5 },
    { label: "★ ★ ★ ★ ☆  (4 Stars)", value: 4 },
    { label: "★ ★ ★ ☆ ☆  (3 Stars)", value: 3 },
    { label: "★ ★ ☆ ☆ ☆  (2 Stars)", value: 2 },
    { label: "★ ☆ ☆ ☆ ☆  (1 Star)", value: 1 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8"
    >
      {/* Testimonials List (2 Columns) */}
      <div className="lg:col-span-2 space-y-4">
        <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold pb-2">
          PUBLISHED TESTIMONIALS ({list.length})
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((t) => (
            <div
              key={t.id}
              className="rounded-2xl border border-amber-400/70 p-6 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 flex flex-col justify-between space-y-3 hover:scale-[1.01] transition-all"
            >
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="text-amber-400 text-sm tracking-widest font-bold">
                    {"★".repeat(t.rating)}
                    <span className="text-white/20">{"★".repeat(5 - t.rating)}</span>
                  </div>
                  <button
                    onClick={() => del(t.id)}
                    className="text-xs border border-red-500/40 bg-red-950/20 hover:bg-red-900/40 text-red-300 px-3 py-1 rounded-lg uppercase tracking-wider font-extrabold transition-all"
                  >
                    Delete
                  </button>
                </div>

                <p className="text-white/90 italic font-heading text-base leading-relaxed">
                  "{t.quote}"
                </p>
              </div>

              <div className="text-xs font-bold text-amber-300/90 border-t border-white/10 pt-2">
                — {t.author}{t.location ? `, ${t.location}` : ""}
              </div>
            </div>
          ))}
          {list.length === 0 && (
            <div className="col-span-full text-center py-16 text-white/50 bg-hpaper border border-white/10 rounded-2xl font-medium">
              No testimonials created yet.
            </div>
          )}
        </div>
      </div>

      {/* Add Testimonial Form Box */}
      <div className="rounded-2xl border border-hgold/70 p-6 lg:p-8 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 space-y-4 h-fit">
        <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold pb-2 border-b border-white/10">
          ADD TESTIMONIAL
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-white/90 block mb-1">Author Name</label>
            <input
              placeholder="e.g. Sanjay M."
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white/90 block mb-1">Location / City</label>
            <input
              placeholder="e.g. Delhi, NCR"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white/90 block mb-1">Rating Stars</label>
            <select
              value={form.rating}
              onChange={(e) => setForm({ ...form, rating: e.target.value })}
              className="w-full bg-black border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
            >
              <option value="5">★ ★ ★ ★ ★  (5 Stars)</option>
              <option value="4">★ ★ ★ ★ ☆  (4 Stars)</option>
              <option value="3">★ ★ ★ ☆ ☆  (3 Stars)</option>
              <option value="2">★ ★ ☆ ☆ ☆  (2 Stars)</option>
              <option value="1">★ ☆ ☆ ☆ ☆  (1 Star)</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-white/90 block mb-1">Customer Quote / Review</label>
            <textarea
              rows="4"
              placeholder="Enter testimonial text..."
              value={form.quote}
              onChange={(e) => setForm({ ...form, quote: e.target.value })}
              className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 outline-none rounded-xl resize-none text-sm text-white transition-all"
            />
          </div>

          <button
            onClick={save}
            className="gold-btn w-full justify-center py-3.5 rounded-xl font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40"
          >
            Add Testimonial
          </button>
        </div>
      </div>
    </motion.div>
  );
}
